/**
 * Who is signed in, and how signing in works on this deployment.
 *
 * The allowlist is the security boundary and it lives on the server (PLAN.md §13). None
 * of this is a gate — it decides what to *render*. Every protected endpoint enforces
 * access on its own, so a tampered client gets 401s rather than data.
 */

import { ApiError, api, post, type Fetch } from '$lib/api/client';

export type Role = 'superadmin' | 'admin';
export type UserStatus = 'invited' | 'active' | 'disabled';

export interface User {
	id: string;
	email: string;
	name: string | null;
	role: Role;
	status: UserStatus;
	last_login_at: string | null;
	created_at: string;
	ai_enabled: boolean;
	ai_model: string | null;
	ai_key_last4: string | null;
	ai_key_verified_at: string | null;
	ai_key_last_error: string | null;
}

export interface LoginMethods {
	google: boolean;
	dev_login: boolean;
}

export const AUTH = 'app:auth';

/** `null` means signed out — an expected state, not an error. */
export async function currentUser(fetch?: Fetch): Promise<User | null> {
	try {
		return await api<User>('/api/auth/me', { fetch });
	} catch (error) {
		if (error instanceof ApiError && error.status === 401) return null;
		throw error;
	}
}

export const loginMethods = (fetch?: Fetch) => api<LoginMethods>('/api/auth/config', { fetch });

/** A full navigation, not fetch: the browser has to follow Google's redirect. */
export function startGoogleLogin(returnTo: string) {
	window.location.href = `/api/auth/login?return_to=${encodeURIComponent(returnTo)}`;
}

export const devLogin = (email: string) => post<User>('/api/auth/dev-login', { email });

export const logout = () => post<void>('/api/auth/logout');

export const isSuperadmin = (user: User | null) => user?.role === 'superadmin';
